import React from "react";

import { faHouse, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  CheckForm,
  Grid,
  Group,
  Section,
  Typography,
} from "@samskip/frontend-components";
import {
  Control,
  DeepRequired,
  FieldErrorsImpl,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { nameof } from "ts-simple-nameof";

import { DomesticConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import { DomesticHelper } from "@assets/helpers";
import { CommonTypes, DomesticTypes } from "@assets/types";
import { getErrorMessage, ObjectUtilities } from "@assets/utilities";

interface PayerSectionProps {
  addresses: DomesticTypes.DomesticPayerLegAddresses;
  control: Control<DomesticFormTypes.CreateForm>;
  disabled?: boolean;
  dropOffMethod: DomesticConstants.DELIVERY_MODE;
  errors: FieldErrorsImpl<DeepRequired<DomesticFormTypes.CreateForm>>;
  onSeparatePayerToggle: CommonTypes.CallbackFunction;
  pickupMethod: DomesticConstants.DELIVERY_MODE;
  register: UseFormRegister<DomesticFormTypes.CreateForm>;
}

const PaymentSection = ({
  addresses,
  control,
  disabled = false,
  dropOffMethod,
  errors,
  onSeparatePayerToggle,
  pickupMethod,
  register,
}: PayerSectionProps) => {
  const { t } = useTranslation();
  const isSeparatePayerLegs = useWatch({
    control,
    name: nameof<DomesticFormTypes.CreateForm>((i) => i.IsSeparatePayerLegs),
  });

  // The payer legs should be disabled if neither pickup and drop-off methods are address deliveries
  // or any of the addresses fields don't have values
  const isPayerLegsSelectable =
    (pickupMethod === DomesticConstants.DELIVERY_MODE.ADDRESS ||
      dropOffMethod === DomesticConstants.DELIVERY_MODE.ADDRESS) &&
    !ObjectUtilities.hasSomeFalsyValues(addresses) &&
    !disabled;

  const getAddressIcon = (type: DomesticConstants.PAYER_TERM_TYPE) => {
    switch (type) {
      case DomesticConstants.PAYER_TERM_TYPE.H2D:
      case DomesticConstants.PAYER_TERM_TYPE.D2H:
        return faHouse;
      case DomesticConstants.PAYER_TERM_TYPE.H2H:
      default:
        return faLocationDot;
    }
  };
  const payerRadioGroup = (
    <Group
      errorMessage={getErrorMessage(
        nameof<DomesticFormTypes.CreateForm>((i) => i.Payer),
        errors
      )}
    >
      <CheckForm
        bordered
        disabled={disabled}
        hasError={errors.Payer != null}
        id="sender_payer"
        label={t(isSeparatePayerLegs ? "LABEL_PAID_BY_SENDER" : "LABEL_SENDER")}
        type="radio"
        value={DomesticConstants.BOOKING_PARTY.SENDER}
        {...register(nameof<DomesticFormTypes.CreateForm>((i) => i.Payer))}
      />

      <CheckForm
        bordered
        disabled={disabled}
        hasError={errors.Payer != null}
        id="receiver_payer"
        label={t(
          isSeparatePayerLegs ? "LABEL_PAID_BY_RECEIVER" : "LABEL_RECEIVER"
        )}
        type="radio"
        value={DomesticConstants.BOOKING_PARTY.RECEIVER}
        {...register(nameof<DomesticFormTypes.CreateForm>((i) => i.Payer))}
      />
    </Group>
  );

  return (
    <Section id="payer" title={t("LABEL_PAYMENT")}>
      {isPayerLegsSelectable && (
        <CheckForm
          id={nameof<DomesticFormTypes.CreateForm>(
            (i) => i.IsSeparatePayerLegs
          )}
          label={t("LABEL_SPLIT_PAYMENT")}
          {...register(
            nameof<DomesticFormTypes.CreateForm>((i) => i.IsSeparatePayerLegs),
            {
              onChange: () => {
                onSeparatePayerToggle();
              },
            }
          )}
        />
      )}
      <Grid mdCol={2}>
        <Grid mdCol={1}>
          {!isSeparatePayerLegs
            ? payerRadioGroup
            : DomesticHelper.generatePayerLegs(
                pickupMethod,
                dropOffMethod,
                addresses
              ).map((payerLeg, index) => (
                <Card id={`leg-${index}`} key={index}>
                  <Group vertical>
                    <Typography
                      color="neutral-900"
                      fontWeight="semibold"
                      icon={getAddressIcon(payerLeg.type)}
                      variant="actions-s"
                    >
                      {payerLeg.title}
                    </Typography>
                    {!payerLeg.payer ? (
                      payerRadioGroup
                    ) : (
                      <Typography color="neutral-900">
                        {payerLeg.payer ===
                        DomesticConstants.BOOKING_PARTY.SENDER
                          ? t("LABEL_PAID_BY_SENDER")
                          : t("LABEL_PAID_BY_RECEIVER")}
                      </Typography>
                    )}
                  </Group>
                </Card>
              ))}
        </Grid>
      </Grid>
    </Section>
  );
};

export default PaymentSection;
